import { type InfiniteData, QueryClient } from '@tanstack/react-query'
import type { CursorPaginationMeta, OffsetPaginationMeta } from './gen'

const queryClientConfig = {
	defaultOptions: {
		queries: {
			networkMode: 'online',
			retry: typeof window === 'undefined' ? 0 : 1,
			retryOnMount: true,
			retryDelay: 1000,
			staleTime: 600_000, //10 min
			refetchOnMount: true,
			refetchOnWindowFocus: false,
			refetchOnReconnect: true
		}
	}
} satisfies ConstructorParameters<typeof QueryClient>[0]

export function createAppQueryClient() {
	return new QueryClient(queryClientConfig)
}

export const infiniteQueryFetchNextPage = async (infiniteQuery: {
	hasNextPage: boolean
	isFetching: boolean
	fetchNextPage: () => Promise<unknown>
}) => {
	if (infiniteQuery.hasNextPage && !infiniteQuery.isFetching) {
		await infiniteQuery.fetchNextPage()
	}
}

// `getNextPageParam` for cursor-paginated endpoints (`CursorPaginationMeta`):
// the opaque `nextCursor`, or `null` on the last page. Pair with
// `initialPageParam: null`.
export const cursorNextPageParam = (lastPage: { meta: CursorPaginationMeta }) =>
	lastPage.meta.nextCursor

// `getNextPageParam` for offset-paginated endpoints (`OffsetPaginationMeta`):
// the next 1-based page number, or `null` on the last page. Pair with
// `initialPageParam: 1`.
export const offsetNextPageParam = (lastPage: {
	meta: OffsetPaginationMeta
}) =>
	lastPage.meta.page < lastPage.meta.totalPages
		? lastPage.meta.page + 1
		: null

// Collapses an infinite query's pages into one list. Belongs in a factory's
// `select` — the one view `select` is allowed to hold that the `queryFn` cannot.
export const flattenInfinitePages = <T>(data: InfiniteData<{ data: T[] }>) =>
	data.pages.flatMap((page) => page.data)
